import React, { useState, useEffect } from "react";
import { components } from "react-select";
import Select from "component/Common/ReactSelectDropdown/Select";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import CheckIcon from "@mui/icons-material/Check";
import { Typography } from "@material-tailwind/react";
import { useDevicePixelRatio } from "helpers/hooks";

const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  selectProps: { value },
  ...rest
}) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#ffffff";
  if (isActive) bg = "#ffffff";

  let colour = "inherit";
  if (isFocused) colour = "#FB6906";
  if (isActive) colour = "#FB6906";

  const style = {
    fontFamily: "Satoshi Variable",
    backgroundColor: isSelected ? "#FFEEE5" : isFocused ? "#F5F5F5" : "#FFFFFF",
    color: isSelected ? "#F46C3C" : "#000000",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingRight: "16px",
    marginBottom: 2,
    display: "flex",
    fontSize: "14px",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    borderRadius: "8px",
    "&:active": {
      backgroundColor: isSelected ? "#FFEEE5" : "#F0F0F0",
    },
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
  };
  return (
    <>
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isFocused={isFocused}
        isSelected={isSelected}
        getStyles={getStyles}
        innerProps={props}
      >
        <AppCheckListTile isChecked={isSelected} label={children} />
      </components.Option>
    </>
  );
};

const ValueContainer = ({ children, ...rest }) => {
  const selectedItem = rest.selectProps.value?.length;
  return [
    <>
      <components.ValueContainer {...rest} className="w-fit h-full !p-0">
        <span className="min-w-20 h-full text-sm text-primary">
          {selectedItem > 0 ? `${selectedItem} selected` : ""}
        </span>
        {React.Children.map(children, (child) => {
          return <>{child}</>;
        })}
      </components.ValueContainer>
    </>,
  ];
};

const DropdownIndicator = (props) => {
  const { selectProps } = props;
  const isValueSelected = selectProps.value.length > 0;
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <ChevronDownIcon
          className={`w-4 h-4 ${isValueSelected ? "text-primary" : "text-grey-600"}`}
        />
      </components.DropdownIndicator>
    )
  );
};

export const MultipleSelectComponent = (props) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [inputValue, setInputValue] = useState();
  const {
    placementTarget = null,
    option,
    name = "",
    onChange,
    placeholder = "",
    autoHeight = false,
    maxMenuHeight = 400,
    isIconWithOption = false,
    value,
  } = props;

  const dpr = useDevicePixelRatio();
  const dprz = dpr === 1.25 || dpr === 2;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderWidth: "0.5px", // ✅ Exact 0.5px border
      borderColor: state.isFocused ? "#F46C3C" : "#D9D9D9", // Focus color
      color: state.isSelected ? "#F46C3C" : "#7E7E7E", // Focus color
      boxShadow: "none", // Remove default focus shadow
      "&:hover": { borderColor: state.isFocused ? "#F46C3C" : "black" }, // Hover effect
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingRight: "12px",
      paddingLeft: isIconWithOption ? "12px" : "16px",
      borderRadius: "12px",
      cursor: "pointer",
      gap: "0px",
      width: "100%",
      // minWidth: "200px",
      maxWidth: "320px",
      flexWrap: "nowrap",
    }),
    placeholder: (styles, { data }) => {
      return {
        ...styles,
        fontStyle: "normal",
        fontWeight: 400,
        fontSize: dprz ? "14px" : "14px",
        color: "#7E7E7E",
        top: autoHeight ? "50%" : "54%",
        marginBottom: "5px",
        minWidth: "120px",
        margin: "0px",
      };
    },
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none",
    }),
    valueContainer: (styles, data) => ({
      ...styles,
      padding: "8px 10px",
      position: "relative",
      marginTop: "0",
      height: "38px",
      fontSize: "14px",
      alignItems: "center",
      overflow: "hidden",
      flexWrap: "nowrap",
      minWidth: "0px !important",
    }),
    multiValue: (styles, state) => ({
      ...styles,
      display: "none",
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: "0px",
      margin: "0px",
      color: "#1C1B1F",
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      margin: "0px",
      padding: "0px",
      alignItems: "center",
      display: "flex",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "0px 8px",
      margin: "0px",
      display: "flex",
      alignItems: "center",
    }),

    menuPortal: (base) => ({ ...base, zIndex: 999 }),
    input: (base) => ({
      ...base,
      width: "100% !important",
      padding: "0px",
      margin: "0px",
    }),
    menu: (base) => ({
      ...base,
      padding: "6px",
      backgroundColor: "#FFFFFF",
      borderRadius: "8px",
      boxShadow: "0px 4px 12px 0px #7E7E7E29",
      minWidth: "248px",
      // Adds a subtle shadow
    }),
    menuList: (base) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "6px",
        height: "0px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#555",
      },
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? "#FFEEE5" : isFocused ? "#F5F5F5" : "#FFFFFF",
      color: isSelected ? "#F46C3C" : "#000000",
      paddingTop: "12px",
      paddingBottom: "16px",
      paddingRight: "16px",
      marginBottom: 2,
      display: "flex",
      fontSize: "14px",
      alignItems: "center",
      gap: "4px",
      cursor: "pointer",
      borderRadius: "8px",
      "&:active": {
        backgroundColor: isSelected ? "#FFEEE5" : "#F0F0F0",
      },
    }),
  };

  const _handleInputChange = (input, { action }) => {
    if (action !== "set-value") {
      setInputValue(input);
      return input;
    }
    return inputValue;
  };

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  return (
    <div className="App">
      <Select
        isMulti
        name={name}
        newStyle={customStyles}
        // menuIsOpen
        // menuPortalTarget={placementTarget}
        menuPosition="fixed"
        menuPlacement="auto"
        inputValue={inputValue}
        closeMenuOnSelect={false}
        placeholder={placeholder}
        hideSelectedOptions={false}
        onChange={(options) => {
          if (Array.isArray(options)) {
            setSelectedOptions(options.map((opt) => opt));
          }
          onChange(options);
        }}
        options={option}
        components={{
          Option: InputOption,
          ValueContainer: ValueContainer,
          DropdownIndicator: DropdownIndicator,
        }}
        value={selectedOptions}
        autoHeight={autoHeight}
        onInputChange={_handleInputChange}
        // styles={customStyles}
      />
    </div>
  );
};

const AppCheckBox = ({ isChecked = false }) => {
  return (
    <div
      className={`w-5 h-5 min-w-5 min-h-5 grid place-items-center mt-[0.5px] ${
        isChecked ? "bg-primary" : "bg-transparent border border-black"
      } rounded-md`}
    >
      {isChecked && <CheckIcon style={{ fontSize: 16, color: "white" }} />}
    </div>
  );
};

const AppCheckListTile = ({ isChecked = false, label = "" }) => {
  return (
    <div className="flex justify-start !gap-3">
      <AppCheckBox isChecked={isChecked} />
      <Typography className="text-sm ">{label}</Typography>
    </div>
  );
};
